/**
 * Positions product pins relative to the visible image area (object-fit: cover),
 * so markers stay fixed on the image at every viewport size.
 */
const PinPositioning = {
  /**
   * @param {Function} fn
   * @param {number} wait
   * @returns {Function}
   */
  debounce(fn, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  },

  /**
   * @param {string} value - CSS object-position value
   * @returns {{ x: { mode: string, value: number }, y: { mode: string, value: number } }}
   */
  parseObjectPosition(value) {
    const parts = (value || 'center center').trim().split(/\s+/);

    return {
      x: this.parseAxis(parts[0] || 'center', 'x'),
      y: this.parseAxis(parts[1] || 'center', 'y'),
    };
  },

  /**
   * @param {string} token
   * @param {'x' | 'y'} axis
   * @returns {{ mode: 'start' | 'center' | 'end', value: number }}
   */
  parseAxis(token, axis) {
    const startKeywords = axis === 'x' ? ['left'] : ['top'];
    const endKeywords = axis === 'x' ? ['right'] : ['bottom'];

    if (startKeywords.includes(token)) return { mode: 'start', value: 0 };
    if (endKeywords.includes(token)) return { mode: 'end', value: 1 };
    if (token === 'center') return { mode: 'center', value: 0.5 };

    if (token.includes('%')) {
      const value = parseFloat(token) / 100;
      if (value <= 0) return { mode: 'start', value: 0 };
      if (value >= 1) return { mode: 'end', value: 1 };
      if (Math.abs(value - 0.5) < 0.01) return { mode: 'center', value: 0.5 };
      if (value < 0.5) return { mode: 'start', value };
      return { mode: 'end', value };
    }

    return { mode: 'center', value: 0.5 };
  },

  /**
   * Maps an admin coordinate (0–100) to a fraction along the visible image axis.
   *
   * @param {number} coord
   * @param {{ mode: string }} axis
   * @returns {number}
   */
  coordToFraction(coord, axis) {
    const value = Number(coord);
    if (Number.isNaN(value)) return 0.5;

    switch (axis.mode) {
      case 'center':
        return 0.5 + (value - 50) / 100;
      case 'end':
        return 1 - value / 100;
      default:
        return value / 100;
    }
  },

  /**
   * @param {HTMLElement} container
   * @param {HTMLImageElement} img
   * @returns {{ left: number, top: number, width: number, height: number } | null}
   */
  getObjectFitCoverBounds(container, img) {
    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;
    if (!naturalWidth || !naturalHeight) return null;

    const width = container.clientWidth;
    const height = container.clientHeight;
    if (!width || !height) return null;

    const imageRatio = naturalWidth / naturalHeight;
    const containerRatio = width / height;
    let renderWidth;
    let renderHeight;

    if (imageRatio > containerRatio) {
      renderHeight = height;
      renderWidth = height * imageRatio;
    } else {
      renderWidth = width;
      renderHeight = width / imageRatio;
    }

    const objectPosition = this.parseObjectPosition(getComputedStyle(img).objectPosition);

    return {
      left: (width - renderWidth) * objectPosition.x.value,
      top: (height - renderHeight) * objectPosition.y.value,
      width: renderWidth,
      height: renderHeight,
    };
  },

  /**
   * @param {HTMLImageElement} img
   * @returns {boolean}
   */
  isImageVisible(img) {
    if (!img || !img.isConnected) return false;
    return getComputedStyle(img).display !== 'none' && img.offsetParent !== null;
  },

  /**
   * @param {HTMLElement} context
   * @returns {HTMLImageElement | null}
   */
  getActiveImage(context) {
    const mobileImage = context.querySelector('img.img-mobile');
    const desktopImage = context.querySelector('img.img-desktop');

    if (mobileImage && this.isImageVisible(mobileImage)) return mobileImage;
    if (desktopImage && this.isImageVisible(desktopImage)) return desktopImage;

    return context.querySelector('.media img, .banner__media img, .slideshow__media img');
  },

  /**
   * @param {HTMLElement} layer
   * @returns {{ container: HTMLElement, img: HTMLImageElement } | null}
   */
  getPinContext(layer) {
    const slide = layer.closest('.slideshow__slide, .multicolumn-card, .multicolumn-list__item');
    const img = this.getActiveImage(slide || layer);
    if (!img) return null;

    const container = img.closest('.media, .banner__media, .slideshow__media');
    if (!container) return null;

    if (layer.parentElement === container) {
      layer.style.top = '0';
      layer.style.left = '0';
      layer.style.width = '100%';
      layer.style.height = '100%';
      return { container, img };
    }

    const layerParent = layer.offsetParent || layer.parentElement;
    if (!layerParent) return null;

    const layerParentRect = layerParent.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    layer.style.top = `${containerRect.top - layerParentRect.top}px`;
    layer.style.left = `${containerRect.left - layerParentRect.left}px`;
    layer.style.width = `${containerRect.width}px`;
    layer.style.height = `${containerRect.height}px`;

    return { container, img };
  },

  /**
   * @param {HTMLElement} pin
   * @returns {void}
   */
  updatePin(pin) {
    const layer = pin.closest('.pin-layer');
    if (!layer) return;

    const context = this.getPinContext(layer);
    if (!context) return;

    const { container, img } = context;
    const bounds = this.getObjectFitCoverBounds(container, img);
    if (!bounds) return;

    const hasMobileImage = pin.dataset.hasMobileImage === 'true';
    const useMobileCoords = hasMobileImage && img.classList.contains('img-mobile');

    const coordX = Number(useMobileCoords ? pin.dataset.xMobile : pin.dataset.x);
    const coordY = Number(useMobileCoords ? pin.dataset.yMobile : pin.dataset.y);
    const objectPosition = this.parseObjectPosition(getComputedStyle(img).objectPosition);

    const fractionX = this.coordToFraction(coordX, objectPosition.x);
    const fractionY = this.coordToFraction(coordY, objectPosition.y);

    const left = bounds.left + bounds.width * fractionX;
    const top = bounds.top + bounds.height * fractionY;

    pin.style.left = `${left}px`;
    pin.style.top = `${top}px`;
    pin.dataset.positioned = 'true';
  },

  /**
   * @param {ParentNode} [root]
   * @returns {void}
   */
  updateAll(root = document) {
    root.querySelectorAll('pin-product').forEach((pin) => this.updatePin(pin));
  },

  /**
   * @returns {void}
   */
  bindGlobalListeners() {
    if (this.globalListenersBound) return;
    this.globalListenersBound = true;

    const update = this.debounce(() => this.updateAll(), 150);
    const bindImageLoads = (root = document) => {
      root.querySelectorAll('.pin-layer img').forEach((img) => {
        if (img.complete) return;
        img.addEventListener('load', () => this.updateAll(), { once: true });
      });
    };

    window.addEventListener('resize', update);
    window.addEventListener('load', () => this.updateAll());

    document.addEventListener('shopify:section:load', (event) => {
      bindImageLoads(event.target);
      this.updateAll(event.target);
    });
    document.addEventListener('shopify:block:select', () => this.updateAll());

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        bindImageLoads();
        this.updateAll();
      });
    } else {
      bindImageLoads();
      this.updateAll();
    }
  },
};

PinPositioning.bindGlobalListeners();
